import React from "react";
import { toParisNaive } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Clock } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Calendar } from "primereact/calendar";

interface SchedulingItem {
    type: 'fixed' | 'before_start' | 'after_start' | 'before_end' | 'after_end';
    time?: string;
    date?: string;
    offset?: number;
    unit?: 'minutes' | 'hours';
}

interface ClassSchedulingConfigProps {
    scheduling: SchedulingItem;
    onChange: (updates: Partial<SchedulingItem>) => void;
    appendTo?: any;
}

const ClassSchedulingConfig: React.FC<ClassSchedulingConfigProps> = ({
    scheduling,
    onChange,
    appendTo = 'self'
}) => {
    // Helper to extract days, hours, and minutes from offset (in minutes)
    const getComponents = (offset: number = 0) => {
        const days = Math.floor(offset / (24 * 60));
        const hours = Math.floor((offset % (24 * 60)) / 60);
        const minutes = offset % 60;
        return { days, hours, minutes };
    };

    const handleComponentUpdate = (part: 'days' | 'hours' | 'minutes', value: number) => {
        const { days, hours, minutes } = getComponents(scheduling.offset || 0);

        let newOffset = 0;
        if (part === 'days') newOffset = value * 24 * 60 + hours * 60 + minutes;
        else if (part === 'hours') newOffset = days * 24 * 60 + value * 60 + minutes;
        else if (part === 'minutes') newOffset = days * 24 * 60 + hours * 60 + value;

        onChange({ offset: newOffset, unit: 'minutes' });
    };

    const { days, hours, minutes } = getComponents(scheduling.offset || 0);

    return (
        <div className="flex items-center gap-2 p-3 bg-slate-50/50 rounded-2xl border border-slate-100 group w-full">
            <div className="h-9 w-9 bg-white rounded-xl flex items-center justify-center shadow-sm text-slate-400 shrink-0">
                <Clock className="h-4 w-4" />
            </div>

            <Select
                value={scheduling.type}
                onValueChange={(val: any) => onChange({ type: val })}
            >
                <SelectTrigger className="h-9 bg-white border-slate-200 text-xs">
                    <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="fixed">À une date fixe</SelectItem>
                    <SelectItem value="before_start">Avant le début du cours</SelectItem>
                    <SelectItem value="after_start">Après le début du cours</SelectItem>
                    <SelectItem value="before_end">Avant la fin du cours</SelectItem>
                    <SelectItem value="after_end">Après la fin du cours</SelectItem>
                </SelectContent>
            </Select>

            {scheduling.type === 'fixed' ? (
                <div className="flex-1">
                    <Calendar
                        id={`fixed-date-picker`}
                        minDate={new Date()}
                        value={scheduling.date ? new Date(scheduling.date) : null}
                        onChange={(e) => {
                            if (e.value instanceof Date) {
                                onChange({ date: toParisNaive(e.value) });
                            } else if (!e.value) {
                                onChange({ date: undefined });
                            }
                        }}
                        showTime
                        showIcon
                        hourFormat="24"
                        className="w-full"
                        appendTo={appendTo}
                        readOnlyInput
                        pt={{
                            input: { className: "p-inputtext h-9 py-0 text-xs border border-slate-200 rounded-md w-full px-2" },

                        }}
                    />
                </div>
            ) : (
                <div className="flex-1 flex items-center gap-1.5">
                    <Select
                        value={String(days)}
                        onValueChange={(val) => handleComponentUpdate('days', parseInt(val))}
                    >
                        <SelectTrigger className="h-9 bg-white border-slate-200 text-[11px] flex-1">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {Array.from({ length: 31 }, (_, i) => i).map(d => (
                                <SelectItem key={d} value={String(d)}>{d} j</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select
                        value={String(hours)}
                        onValueChange={(val) => handleComponentUpdate('hours', parseInt(val))}
                    >
                        <SelectTrigger className="h-9 bg-white border-slate-200 text-[11px] flex-1">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {Array.from({ length: 24 }, (_, i) => i).map(h => (
                                <SelectItem key={h} value={String(h)}>{h} h</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select
                        value={String(minutes)}
                        onValueChange={(val) => handleComponentUpdate('minutes', parseInt(val))}
                    >
                        <SelectTrigger className="h-9 bg-white border-slate-200 text-[11px] flex-1">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {Array.from({ length: 12 }, (_, i) => i * 5).map(m => (
                                <SelectItem key={m} value={String(m)}>{m} min</SelectItem>
                            ))}
                            <SelectItem value="1">1 min</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            )}
        </div>
    );
};

export default ClassSchedulingConfig;
